import { Routes, Route, Navigate, NavLink, useNavigate } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import { Button } from "react-bootstrap"
import {
  FiHome,
  FiLogOut,
  FiShield,
  FiCheckSquare,
  FiList,
  FiDroplet,
  FiPackage,
  FiBook,
  FiAlertCircle,
  FiMessageSquare,
  FiSliders,
  FiClipboard,
  FiUsers,
  FiCheckCircle,
  FiTarget,
  FiInbox,
} from "react-icons/fi"

import "./index.scss"
import useT from "utils/hooks/useT"
import homePath from "utils/scripts/homeRoute"
import { HelpButton } from "components"
import { auth } from "state_management"
import { selectCanViewQaTeam, selectCanAuthorMasterData } from "state_management/permissions"
import LimsPlaceholder from "./LimsPlaceholder"
import AuditViewer from "./AuditViewer"
import ConsumablesRegister from "./ConsumablesRegister"
import ProductTestPlan from "./ProductTestPlan"
import QaAnalysisReview from "./QaAnalysisReview"
import QaSampleReview from "./QaSampleReview"
import BatchHistory from "./BatchHistory"
import Investigations from "./Investigations"
import AnalysisDocumentsRegister from "./AnalysisDocumentsRegister"
import CommentLibrary from "./CommentLibrary"
import SstTemplates from "./SstTemplates"
import WorklistTemplatesRegister from "./WorklistTemplatesRegister"
import QaTeam from "./QaTeam"
import ChangeControl from "./ChangeControl"
import AnalysisDefinitionReadOnly from "./AnalysisDefinitionReadOnly"
import AnalysisSpecifications from "./AnalysisSpecifications"

// The LIMS workspace sections. The section content plugs in exactly here; any section without a
// Component falls back to a "coming soon" placeholder.
//
// SPEC-011 removed the standalone Review queue (review now happens inline on the analysis task,
// surfaced via the right-panel Reviews inbox) and the OOS register screen. The limsOos slice was
// subsequently REVIVED by SPEC-018 (PRD-005 Area 4) as the structured two-phase OOS INVESTIGATION
// (opened from a FAIL result via the investigation modal, no standalone menu section).
// SPEC-012 replaced the OOS-keyed "QA decisions" screen with the "QA Analysis Review" worklist,
// which keys off results sent to QA (state "sent-to-qa") and decides per sample.
// SPEC-013 (A3) reworked the "QA Release" queue into "QA Batch Release" (the lot is releasable once
// all its analyses are QA-approved) and REMOVED the standalone CoA register — the batch certificate
// is reachable from the Sample View and from the QA Batch Release "Released" table.
// SPEC-022 (K10) added a standalone "Investigations" menu item: the team's entry to OPEN OOS
// investigations (Phase 1 is lab work). QA Analysis Review now shows only the subset QA must decide.
// SPEC-038 (F6) RETIRED the per-analysis "QA Analysis Review" worklist and replaced it with the
// sample/lot-level "QA SampleReview" (QaSampleReview): QA's unit is now the samplenr/lotnr, listed
// once it is COMPLETE across all its analysis runs, with Approve/Reject + the QA e-signature here.
// The old qa-analysis-review path redirects below for bookmarks; per-analysis QA decisions still
// live inline on the analysis (QaDecisionSection in "QC Results & Approvals").
// `requiresAuthor` (SPEC-058 B / BE SPEC-059) gates a section to the PLANNER — master-data AUTHORING
// is a role, approving is a qualification: the comment library has nothing to approve and nothing
// for others to see, so it stays planner-only; the SST and worklist template registers are now open
// to every LIMS user (read + the "awaiting approval" pointer), with their authoring buttons
// planner-only inside. Approval of ALL master data happens in the one Change-control section.
// `requiresQaTeamView` (SPEC-058 A) gates the QA-team page to the QA Manager (who acts there) and the
// Planner (read-only). All other sections are open to any LIMS user.
// R8 (#4087): the order is the ORDER OF WORK, not the order the sections were built in. Daily QA
// queues first (sample review — which since SPEC-064 also carries the holds that block it — then
// investigations), then the stamdata registers a QA-admin maintains (consumables, SST templates,
// worklist templates, analysis documents, comment library), then the read-only overviews (product
// readiness, batch history, audit trail).
// SPEC-064 (#4157) split the two QA menu items by ACTION rather than by object: everything QA can act
// on is in QA Review, everything that only records what happened is in Batch History. The QA Batch
// Holds section is therefore GONE — its active holds went to QA Review, its lifted ones to Batch
// History — and `qa-batch-release` is now one of the redirects below, so bookmarks survive (R12).
// Batch History sits second-to-last, immediately above the audit trail: it is a read-only lookup
// surface, which by the ordering principle above belongs at the bottom.
// SPEC-070 (#4223) brings the per-analysis list back as "QA Analysis", FIRST in the menu — for the
// DECISION step only (release stays sample-level, SPEC-038). QA's two jobs follow the order of the
// work: decide on each analysis' results (QA Analysis), then release the finished lot (QA Review),
// with the investigations that can block either right after.
const SECTIONS = [
  { path: "qa-analysis", labelKey: "limsQaAnalysisReview", Icon: FiInbox, Component: QaAnalysisReview },
  { path: "qa-sample-review", labelKey: "limsQaSampleReview", Icon: FiCheckSquare, Component: QaSampleReview },
  { path: "investigations", labelKey: "limsInvestigations", Icon: FiAlertCircle, Component: Investigations },
  // SPEC-058 B (#4115): the ONE place master data is approved — replaces the approve buttons that
  // lived on the planner-only Setup definition page and in three registers.
  { path: "change-control", labelKey: "limsChangeControl", Icon: FiCheckCircle, Component: ChangeControl },
  // SPEC-063 (#4149): the parameter specifications — where they are created and seen. It leads
  // the master-data registers because it IS the QC method: the limits every result is judged
  // against. Open to every LIMS user (a signer must be able to read what they will sign);
  // authoring is planner-only INSIDE the screen, as for the SST and worklist registers.
  { path: "analysis-specs", labelKey: "limsAnalysisSpecsTitle", Icon: FiTarget, Component: AnalysisSpecifications },
  { path: "consumables", labelKey: "limsConsumables", Icon: FiDroplet, Component: ConsumablesRegister },
  { path: "sst-templates", labelKey: "limsSstTemplatesTitle", Icon: FiSliders, Component: SstTemplates },
  { path: "worklist-templates", labelKey: "limsWorklistTemplatesTitle", Icon: FiClipboard, Component: WorklistTemplatesRegister },
  { path: "analysis-documents", labelKey: "limsForskrifter", Icon: FiBook, Component: AnalysisDocumentsRegister },
  { path: "comment-library", labelKey: "limsCommentLibrary", Icon: FiMessageSquare, Component: CommentLibrary, requiresAuthor: true },
  // SPEC-058 A (#4115): the quality column's staff page — the QA Manager's replacement for the
  // Setup › Team entry they used to get (where every planning field was locked for them).
  { path: "qa-team", labelKey: "limsQaTeam", Icon: FiUsers, Component: QaTeam, requiresQaTeamView: true },
  { path: "products", labelKey: "limsProductReadiness", Icon: FiPackage, Component: ProductTestPlan },
  { path: "batch-history", labelKey: "limsBatchHistory", Icon: FiShield, Component: BatchHistory },
  { path: "audit", labelKey: "limsAudit", Icon: FiList, Component: AuditViewer },
]

// #4131 — the LIMS area opens on the FIRST section, the QA sample review queue, not on Product
// readiness. R8 already ordered SECTIONS by the order of WORK (daily QA queues first, read-only
// overviews last); the default pointed at one of the overviews, so every LIMS user landed on a
// read-only page and had to navigate to the work. Kept as a path constant so the redirects below
// stay in one place.
// SPEC-070 moved the first section to QA Analysis, so the default follows it (same rule).
const DEFAULT_SECTION = "/lims/qa-analysis"

// Dedicated LIMS workspace. Mounted at /lims/* (gated by showLims in App.jsx) and owns
// its own sub-navigation, mirroring how LibrarianScreen owns its layout + nested Routes.
export default function LimsScreen() {
  const canAuthor = useSelector(selectCanAuthorMasterData)
  const canViewQaTeam = useSelector(selectCanViewQaTeam)
  // Gated sections drop out of both the routes and the sidebar for those without the power (the
  // route is simply not registered, so a deep link falls through to the catch-all redirect).
  const visibleSections = SECTIONS.filter(
    (section) => (!section.requiresAuthor || canAuthor) && (!section.requiresQaTeamView || canViewQaTeam)
  )
  return (
    <div className="LimsScreen">
      <LimsSidebar sections={visibleSections} />
      <div className="LimsScreen-main">
        <LimsToolbar />
        <div className="LimsScreen-content">
          <Routes>
            <Route path="/" element={<Navigate to={DEFAULT_SECTION} replace />} />
            {/* The reagents register was generalised to the consumables register (PRD-003
                Area 3); keep the old path working for existing bookmarks / deep links. */}
            <Route path="reagents" element={<Navigate to="/lims/consumables" replace />} />
            {/* "Method documents" (forskrifter) was renamed to "Analysis documents" and inverted
                to be analysis-first (SPEC-014); keep the old path working for bookmarks. */}
            <Route path="forskrifter" element={<Navigate to="/lims/analysis-documents" replace />} />
            {/* The OOS-keyed "QA decisions" screen → result-keyed "QA Analysis Review" (SPEC-012) →
                sample/lot-level "QA SampleReview" (SPEC-038). Old paths redirect for bookmarks.
                SPEC-070 revived the per-analysis list at "qa-analysis", so its old path goes back
                there; "qa-decisions" stays on the release queue it has pointed at since SPEC-038. */}
            <Route path="qa-decisions" element={<Navigate to="/lims/qa-sample-review" replace />} />
            <Route path="qa-analysis-review" element={<Navigate to="/lims/qa-analysis" replace />} />
            {/* "QA Release" was reworked into "QA Batch Release" (SPEC-013), whose screen SPEC-064
                retired: the release action itself has lived on QA Review since SPEC-038, so both old
                paths now land there — pointed straight at it rather than chaining one redirect
                through the other. */}
            <Route path="qa-release" element={<Navigate to="/lims/qa-sample-review" replace />} />
            <Route path="qa-batch-release" element={<Navigate to="/lims/qa-sample-review" replace />} />
            {/* The standalone CoA register was removed (SPEC-013) and R10 removed the "Released"
                table it used to land beside, so this redirect has since resolved to a screen with no
                certificate on it. SPEC-064 gives it a home again: the released lots and their
                certificate download are on Batch History. */}
            <Route path="coa" element={<Navigate to="/lims/batch-history" replace />} />
            {/* SPEC-058 B2: the read-only analysis definition, opened from Change control / the inbox so
                a signer never approves blind. Not a sidebar item; open to every LIMS user. */}
            <Route path="analysis-definition/:analysisDefinitionId" element={<AnalysisDefinitionReadOnly />} />
            {visibleSections.map((section) => (
              <Route
                key={section.path}
                path={section.path}
                element={
                  section.Component ? <section.Component /> : <LimsPlaceholder titleKey={section.labelKey} />
                }
              />
            ))}
            <Route path="*" element={<Navigate to={DEFAULT_SECTION} replace />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}

function LimsSidebar({ sections }) {
  const { t } = useT("common")
  return (
    <nav className="LimsScreen-sidebar">
      <div className="LimsScreen-sidebar-title">{t("lims")}</div>
      {sections.map(({ path, labelKey, Icon }) => (
        <NavLink
          key={path}
          to={`/lims/${path}`}
          className={({ isActive }) => "LimsScreen-navitem" + (isActive ? " active" : "")}
        >
          <Icon />
          <span>{t(labelKey)}</span>
        </NavLink>
      ))}
    </nav>
  )
}

// Top toolbar mirroring the Librarian toolbar: Home centered, Help + Exit on the right.
// Home, Help and Exit reuse the app's existing navigation/logout functions.
function LimsToolbar() {
  const { t } = useT("common")
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isMember = useSelector((state) => state.auth.isMember)
  const memberId = useSelector((state) => state.auth.memberId)
  const directToMemberPlan = useSelector((state) => state.teamStatus.directToMemberPlan)
  // #4168 — a member hidden on the plan has no personal plan to send them to.
  const showOnPlan = useSelector((state) => (memberId && state.members[memberId] ? state.members[memberId].showOnPlan : true))
  const gmpRelaxed = useSelector((state) => state.auth.gmpControlsRelaxed) // SPEC-025: dev-only badge

  const handleLogout = () => {
    dispatch(auth.logout()).then(() => navigate("/login"))
  }

  return (
    <div className="LimsScreen-toolbar">
      {gmpRelaxed && (
        <div className="LimsScreen-toolbar-devbadge">
          <FiAlertCircle />
          <span>{t("limsGmpRelaxedBadge")}</span>
        </div>
      )}
      <div className="LimsScreen-toolbar-nav">
        <Button
          variant="primary"
          onClick={() => navigate(homePath({ isMember, directToMemberPlan, memberId, showOnPlan }))}
        >
          <FiHome title={t("home")} />
        </Button>
      </div>
      <div className="LimsScreen-toolbar-buttons">
        <HelpButton area="lims" />
        <Button variant="primary" onClick={handleLogout} title={t("logout")}>
          <FiLogOut />
        </Button>
      </div>
    </div>
  )
}
